// business-dashboard/src/pages/business/BlockchainPlansPage.tsx
//
// Pricing & request page for blockchain features.
// - Free customers see 3 tier cards + "Request" buttons
// - Pending requests show review status
// - Subscribed customers see the settings panel + usage meter

import React, { useEffect, useState } from "react";
import {
  Shield,
  Zap,
  Crown,
  CheckCircle,
  Lock,
  Send,
  AlertCircle,
  X,
  Clock,
} from "lucide-react";
import BusinessApiService from "../../services/BusinessApiService";
import { getCurrentBusinessId } from "../../services/api";
import { useBlockchainAccess } from "../../hooks/useBlockchainAccess";
import { BlockchainSettingsPanel } from "../../components/blockchain/BlockchainComponents";

const API_BASE = process.env.REACT_APP_API_URL || "";

type RequestedTier = "basic" | "premium" | "enterprise";

interface BlockchainRequest {
  requestedTier?: RequestedTier;
  status: "none" | "pending" | "approved" | "rejected";
  message?: string;
  requestedAt?: string;
  reviewedAt?: string;
  rejectionReason?: string;
}

// ────────────────────────────────────────────────
// Tier definitions
// ────────────────────────────────────────────────
const TIERS = [
  {
    id: "basic" as RequestedTier,
    name: "Basic",
    icon: Shield,
    price: "$29",
    period: "/month",
    color: "blue",
    anchors: "10",
    description: "Get started with blockchain verification",
    features: [
      "10 batch anchors per month",
      "Polygon Amoy testnet",
      "Tamper-proof verification badge",
      "Basic email support",
    ],
  },
  {
    id: "premium" as RequestedTier,
    name: "Premium",
    icon: Zap,
    price: "$99",
    period: "/month",
    color: "purple",
    anchors: "100",
    description: "Most popular for growing brands",
    features: [
      "100 batch anchors per month",
      "Polygon mainnet",
      "Premium verification badge",
      "Priority email + chat support",
      "Custom traceability URLs",
    ],
    popular: true,
  },
  {
    id: "enterprise" as RequestedTier,
    name: "Enterprise",
    icon: Crown,
    price: "Custom",
    period: "",
    color: "amber",
    anchors: "Unlimited",
    description: "Custom solutions for large brands",
    features: [
      "Unlimited batch anchors",
      "Custom smart contracts",
      "Dedicated wallet & RPC",
      "24/7 dedicated support",
      "SLA & compliance reports",
    ],
  },
];

const colorClasses: Record<string, any> = {
  blue: {
    border: "border-blue-200",
    bg: "bg-blue-50",
    accent: "bg-blue-600",
    accentHover: "hover:bg-blue-700",
    text: "text-blue-700",
    icon: "bg-blue-600",
  },
  purple: {
    border: "border-purple-300",
    bg: "bg-purple-50",
    accent: "bg-purple-600",
    accentHover: "hover:bg-purple-700",
    text: "text-purple-700",
    icon: "bg-purple-600",
  },
  amber: {
    border: "border-amber-200",
    bg: "bg-amber-50",
    accent: "bg-amber-600",
    accentHover: "hover:bg-amber-700",
    text: "text-amber-700",
    icon: "bg-amber-600",
  },
};

// ════════════════════════════════════════════════════════
// Main Page Component
// ════════════════════════════════════════════════════════
export default function BlockchainPlansPage() {
  const blockchain = useBlockchainAccess();
  const [request, setRequest] = useState<BlockchainRequest>({ status: "none" });
  const [loadingRequest, setLoadingRequest] = useState(true);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [message, setMessage] = useState<string>("");
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Fetch current request status on mount
  useEffect(() => {
    fetchRequestStatus();
  }, []);

  const fetchRequestStatus = async () => {
    try {
      const businessId = getCurrentBusinessId();
      const token = BusinessApiService.getBusinessToken();
      const res = await fetch(`${API_BASE}/business/${businessId}/blockchain`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      // Server returns business.blockchain — we need the request from a separate call
      // Using profile endpoint for the request data
      const profileRes = await fetch(`${API_BASE}/business/${businessId}/public`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const profileData = await profileRes.json();
      const req = profileData?.business?.blockchainRequest;
      if (req) setRequest(req);
    } catch (e) {
      console.warn("Failed to fetch request status:", e);
    } finally {
      setLoadingRequest(false);
    }
  };

  // ─── Submit a new request ───
  const handleRequest = async (tier: RequestedTier) => {
    setSubmitting(tier);
    setFeedback(null);
    try {
      const businessId = getCurrentBusinessId();
      const token = BusinessApiService.getBusinessToken();
      const res = await fetch(`${API_BASE}/business/${businessId}/blockchain/request`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ requestedTier: tier, message }),
      });
      const data = await res.json();
      if (data.success) {
        setFeedback({
          type: "success",
          text: data.message || "Request submitted successfully!",
        });
        setRequest(data.request);
        setMessage("");
      } else {
        setFeedback({ type: "error", text: data.message || "Submission failed" });
      }
    } catch (e: any) {
      setFeedback({ type: "error", text: e.message });
    }
    setSubmitting(null);
  };

  // ─── Cancel pending request ───
  const handleCancel = async () => {
    if (!window.confirm("Cancel your pending request?")) return;
    try {
      const businessId = getCurrentBusinessId();
      const token = BusinessApiService.getBusinessToken();
      const res = await fetch(`${API_BASE}/business/${businessId}/blockchain/request`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setFeedback({ type: "success", text: "Request cancelled" });
        setRequest({ status: "none" });
      } else {
        setFeedback({ type: "error", text: data.message });
      }
    } catch (e: any) {
      setFeedback({ type: "error", text: e.message });
    }
  };

  // ════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════

  if (blockchain.loading || loadingRequest) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
      </div>
    );
  }

  // ─── Already subscribed → show settings panel ───
  if (blockchain.subscribed) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">⛓ Blockchain Verification</h1>
          <p className="text-gray-500 mt-1">Manage your blockchain anchoring settings</p>
        </div>
        <BlockchainSettingsPanel />
      </div>
    );
  }

  // ─── Free tier → show pricing + request UI ───
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">⛓ Blockchain Verification</h1>
        <p className="text-gray-600">
          Add tamper-proof verification to your products. Customers can independently verify authenticity.
        </p>
      </div>

      {/* Current plan banner */}
      <div className="bg-gray-100 border border-gray-200 rounded-xl p-4 text-center">
        <p className="text-sm text-gray-600">
          Your current plan: <span className="font-bold text-gray-900">🆓 Free (database-only)</span>
        </p>
      </div>

      {/* Feedback message */}
      {feedback && (
        <div
          className={`p-4 rounded-lg flex items-start gap-3 ${
            feedback.type === "success"
              ? "bg-green-50 border border-green-200 text-green-800"
              : "bg-red-50 border border-red-200 text-red-800"
          }`}
        >
          {feedback.type === "success" ? (
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          )}
          <span className="text-sm">{feedback.text}</span>
        </div>
      )}

      {/* Pending request */}
      {request.status === "pending" && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Clock className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-yellow-900">
                  Request Pending: {request.requestedTier?.toUpperCase()} Tier
                </h3>
                <p className="text-sm text-yellow-800 mt-1">
                  Your request is being reviewed. We typically respond within 1-2 business days.
                </p>
                {request.requestedAt && (
                  <p className="text-xs text-yellow-700 mt-2">
                    Submitted {new Date(request.requestedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 text-sm border border-yellow-300 rounded-lg hover:bg-yellow-100 text-yellow-800 flex-shrink-0"
            >
              <X className="w-4 h-4 inline mr-1" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Rejected notice */}
      {request.status === "rejected" && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <X className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-red-900">Previous request was rejected</h3>
              <p className="text-sm text-red-800 mt-1">
                Reason: {request.rejectionReason || "No reason provided"}
              </p>
              <p className="text-xs text-red-700 mt-2">You can submit a new request below.</p>
            </div>
          </div>
        </div>
      )}

      {/* Pricing cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TIERS.map((tier) => {
          const Icon = tier.icon;
          const colors = colorClasses[tier.color];
          const isSubmitting = submitting === tier.id;
          const isPending = request.status === "pending";

          return (
            <div
              key={tier.id}
              className={`relative rounded-2xl border-2 ${colors.border} ${colors.bg} p-6 flex flex-col ${
                tier.popular ? "ring-2 ring-purple-500 ring-offset-2" : ""
              }`}
            >
              {tier.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  MOST POPULAR
                </span>
              )}

              {/* Icon */}
              <div className={`${colors.icon} text-white rounded-xl p-3 w-fit mb-4`}>
                <Icon className="w-6 h-6" />
              </div>

              {/* Name + price */}
              <h3 className="text-2xl font-bold text-gray-900">{tier.name}</h3>
              <div className="mt-2 mb-1">
                <span className="text-3xl font-bold text-gray-900">{tier.price}</span>
                <span className="text-gray-500 text-sm">{tier.period}</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">{tier.description}</p>

              {/* Anchor count */}
              <div className={`text-center py-3 ${colors.bg} border-t border-b ${colors.border} mb-4`}>
                <div className="text-2xl font-bold text-gray-900">{tier.anchors}</div>
                <div className="text-xs text-gray-600 uppercase">Anchors / month</div>
              </div>

              {/* Features list */}
              <ul className="space-y-2 mb-6 flex-1">
                {tier.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${colors.text}`} />
                    {feat}
                  </li>
                ))}
              </ul>

              {/* CTA button */}
              <button
                onClick={() => handleRequest(tier.id)}
                disabled={isSubmitting || isPending}
                className={`w-full ${colors.accent} ${colors.accentHover} text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
              >
                {isSubmitting ? (
                  <>Submitting...</>
                ) : isPending ? (
                  <>Request Pending</>
                ) : tier.id === "enterprise" ? (
                  <>
                    <Send className="w-4 h-4" />
                    Contact Us
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Request Access
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Optional message */}
      {request.status !== "pending" && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-2">📝 Tell us about your needs (optional)</h3>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Tell us how you'd like to use blockchain verification, expected volume, special requirements..."
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
          <p className="text-xs text-gray-500 mt-2">
            This message will be sent with your request to help us understand your needs.
          </p>
        </div>
      )}

      {/* FAQ / Why blockchain */}
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-2xl p-6">
        <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
          <Lock className="w-5 h-5" />
          Why add blockchain verification?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-purple-800">
          <div>
            <div className="font-semibold mb-1">🛡️ Tamper-proof</div>
            <p>Once anchored, batch records can never be changed. Even by us.</p>
          </div>
          <div>
            <div className="font-semibold mb-1">🔍 Independent verification</div>
            <p>Customers can verify authenticity without trusting JepTags.</p>
          </div>
          <div>
            <div className="font-semibold mb-1">📈 Trust & premium positioning</div>
            <p>Stand out from competitors. Justify premium pricing.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
